import PropTypes from 'prop-types';
import React from 'react';
import shortid from 'shortid';
import { EmitMetric } from '../../../Analytics/VisibilityPixel/VisibilityPixel';

import './ProfileFieldList.scss';
import ResumeDownloadButton from '../../../Users/JobProfile/ResumeDownloadButton/ResumeDownloadButton';
import Tag from '../../../Elements/Tag/Tag';
import { intToMonth, PurpleCheck } from '../../../../utilities/ui_utilities';
import slugify from '../../../../utilities/slugify';
import FGBLabel from '../../../Elements/FGBLabel/FGBLabel';
import SVGFromCloudinary from '../../../Elements/SVGFromCloudinary/SVGFromCloudinary';
import ReactionIcons from '../../../Community/SocialBar/ReactionIcons/ReactionIcons';
import { JobApplyContext } from '../../../../contexts/ContextCreator';
import ResponsivePicture from '../../../Elements/ResponsivePicture/ResponsivePicture';
import VCFProfileCard from '../VCFProfileCard/VCFProfileCard';

const fieldLabels = {
    linkedInUrl: 'linkedin',
    professionalSummary: 'professional summary',
    experience: 'experience',
    education: 'education',
    interest: 'skills',
    funQuestion: 'Why this candidate stands out',
};

const convertBoolToYesNo = (bool) => (bool ? 'Yes' : 'No');

export default class ProfileFieldList extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            saved: props.isSaved || false,
        };
    }

    static propTypes = {
        profileFields: PropTypes.shape({
            linkedInUrl: PropTypes.string,
            professionalSummary: PropTypes.string,
            experience: PropTypes.array,
            education: PropTypes.array,
            interest: PropTypes.array,
            funQuestion: PropTypes.string,
        }).isRequired,
        isSaved: PropTypes.bool,
        canMessage: PropTypes.bool,
        companyId: PropTypes.number,
        profileHash: PropTypes.string,
        isAdmin: PropTypes.bool,
        vcfPreferenceProps: {
            email: PropTypes.string,
            jobProfileHash: PropTypes.string,
            attendedVCFs: PropTypes.array,
            authorizedToWorkInUS: PropTypes.bool,
            requiresSponsorship: PropTypes.bool,
            isCurrentStudent: PropTypes.bool,
            yearsOfExperience: PropTypes.string,
        },
    };

    static defaultProps = {
        isSaved: false,
        canMessage: false,
        profileHash: null,
        companyId: null,
    };

    static contextType = JobApplyContext;

    emitMetric = (type) => {
        EmitMetric({ misc_event_type: type, misc_event_count: 1 });
    };

    parseProfileFields = (profileFields) => {
        const labelKeys = Object.keys(fieldLabels);
        return Object.keys(profileFields)
            .filter(
                (k) =>
                    ((profileFields[k] &&
                        profileFields[k] !== null &&
                        profileFields[k].length > 0) ||
                        typeof profileFields[k] === 'boolean') &&
                    fieldLabels[k]
            ) // remove fields with null value
            .sort((a, b) => {
                const orderedKeys = [
                    'professionalSummary',
                    'funQuestion',
                    'experience',
                    'education',
                    'interest',
                ];
                return orderedKeys.indexOf(a) - orderedKeys.indexOf(b);
            }) // sort to match label object order
            .map((fieldKey) => {
                const itemKey = shortid();
                const fieldValue = profileFields[fieldKey]; // field value (email, summary, etc)
                const label = fieldLabels[fieldKey]; // formatted field label
                const labelIcon = fieldKey === 'funQuestion' && (
                    <ResponsivePicture
                        className={'fun-question-icon'}
                        breakpoints={{
                            mobile: {
                                width: 27,
                                height: 25,
                                transforms: { crop: 'limit' },
                            },
                        }}
                        cloudinaryID={'/notifications/star_illo.png'}
                    />
                );
                let labelElement = (
                    <span className="field-label">
                        {label}
                        {labelIcon}
                    </span>
                );
                let fieldElement = <div />;

                switch (true) {
                    case fieldKey === 'experience' || fieldKey === 'education':
                        fieldElement = this.generateExperienceEducationBoxes(
                            fieldValue,
                            label
                        );
                        break;
                    case fieldKey === 'interest':
                        fieldElement = this.generateSkill(fieldValue);
                        break;
                    default:
                        fieldElement = this.constructField(label, fieldValue);
                }

                const isProfessionalSummary =
                    label === fieldLabels.professionalSummary;
                const itemClass = isProfessionalSummary
                    ? 'profile-field summary-item'
                    : 'profile-field';
                const flexColumnClass =
                    fieldKey === 'experience' ||
                    fieldKey === 'education' ||
                    fieldKey === 'funQuestion'
                        ? 'flexColumnStyling'
                        : '';
                const topBorder =
                    fieldKey === 'experience' ||
                    fieldKey === 'education' ||
                    fieldKey === 'interest'
                        ? 'no-border'
                        : '';

                return (
                    <li
                        key={itemKey}
                        className={`${itemClass} ${flexColumnClass} ${topBorder}`}
                    >
                        {labelElement}
                        {fieldElement}
                    </li>
                );
            });
    };

    constructField = (label, text) => {
        switch (label) {
            case fieldLabels.linkedInUrl: // handle user linkedin url styling
                return (
                    <span className="field-value">
                        <a
                            className="user-linkedin"
                            href={text}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() =>
                                this.emitMetric(
                                    'published-job-profile-linkedin-url-click'
                                )
                            }
                        >
                            {text}
                        </a>
                    </span>
                );
            case fieldLabels.professionalSummary: // handle summary section
                return <p className="field-value summary-text">{text}</p>;
            case fieldLabels.funQuestion:
                return (
                    <p className="fun-question-text">
                        <mark className="hightlight">{`"${text}"`}</mark>
                    </p>
                );

            default:
                return <span className="field-value">{text}</span>;
        }
    };

    generateExperienceEducationBoxes = (values, type) => {
        return (
            <section className="experience-education-box-section">
                {values.map((value, idx) => {
                    const {
                        title,
                        secondaryTitle,
                        locationName,
                        dateString,
                        isCurrentText,
                        description,
                    } = this.deriveDisplayText(value, type);
                    const key = `${slugify(
                        title || parseInt(Date.now(), 32)
                    )}-${idx}`;
                    const firstBoxClass = idx === 0 ? 'firstBox' : '';
                    return (
                        <div
                            key={key}
                            className={`experience-education-box ${firstBoxClass}`}
                        >
                            <dt className={`experience-education-title`}>
                                {title || ''}
                            </dt>
                            <dd className={`experience-education-description`}>
                                <div className="sub-title-section">
                                    <p
                                        className={`experience-education-description-venue`}
                                    >
                                        {secondaryTitle}
                                    </p>
                                    {secondaryTitle && locationName ? (
                                        <p className={'subtitle-separator'}>
                                            |
                                        </p>
                                    ) : null}
                                    <p
                                        className={`experience-education-description-location`}
                                    >
                                        {locationName}
                                    </p>
                                </div>
                                <p
                                    className={`experience-education-description-dates`}
                                >
                                    {dateString}
                                </p>
                                {isCurrentText && isCurrentText.length && (
                                    <div className="purple-check-display">
                                        <PurpleCheck />
                                        <p className="is-current-text">
                                            {isCurrentText}
                                        </p>
                                    </div>
                                )}
                                {description && description.length > 0 && (
                                    <p
                                        className={`experience-education-description-description`}
                                    >
                                        {description}
                                    </p>
                                )}
                            </dd>
                        </div>
                    );
                })}
            </section>
        );
    };

    //update to account for non-required start month/year
    deriveDateStrings = (
        isCurrent,
        startMonth,
        startYear,
        endMonth,
        endYear
    ) => {
        if (isCurrent && startYear) {
            return `${startMonth ? startMonth && intToMonth(startMonth) : ''} ${
                startYear || ''
            } – Present`;
        }

        if (startYear && endYear) {
            return `${startMonth ? startMonth && intToMonth(startMonth) : ''} ${
                startYear || ''
            } – ${(endMonth && intToMonth(endMonth)) || ''} ${endYear || ''}`;
        }

        return null;
    };

    deriveDisplayText = (value, type) => {
        if (type === 'experience') {
            const {
                title = null,
                location,
                company: { companyName: secondaryTitle },
                startMonth,
                endMonth,
                startYear,
                endYear,
                isCurrent,
                isRemote,
                description,
            } = value;

            return {
                title,
                secondaryTitle,
                dateString: this.deriveDateStrings(
                    isCurrent,
                    startMonth,
                    startYear,
                    endMonth,
                    endYear
                ),
                locationName: isRemote ? 'Remote' : location && location.name,
                isCurrentDisplay: '',
                description,
            };
        } else if (type === 'education') {
            const {
                school: { name: title = null, parent = null },
                location,
                degree = '',
                field,
                startYear,
                endYear,
                isCurrent,
                isRemote,
                description,
            } = value;

            return {
                title: (parent && parent.name) || title,
                secondaryTitle: `${degree}${field ? `, ${field}` : ''}`,
                dateString: this.deriveDateStrings(
                    isCurrent,
                    null,
                    startYear,
                    null,
                    endYear
                ),
                locationName: isRemote ? 'Remote' : location && location.name,
                isCurrentText:
                    isCurrent && 'I am currently attending this school',
                description,
            };
        }

        console.error(
            'Unknown modalType supplied to method deriveDisplayTextFromModal',
            modalType
        );
    };

    generateSkill = (interests) => {
        return (
            <section className="experience-education-box-section">
                {interests.map((interest) => {
                    return (
                        <Tag shouldAllowDelete={false} tagCssClass={'pinkTag'}>
                            {interest.name}
                        </Tag>
                    );
                })}
            </section>
        );
    };

    hasButtonSection = () => {
        return this.props.canMessage || !this.props.isAdmin;
    };

    buttonSection = (profileHash, isTop = false) => {
        const topStyling = isTop ? 'top-button-section-styling' : '';
        const hasVcfPropsClass = this.props.hasVcfProps
            ? 'no-bottom-border'
            : '';
        return (
            <li
                className={`profile-field no-border ${topStyling} ${hasVcfPropsClass} button-section`}
            >
                {this.props.canMessage && (
                    <a
                        href={`/employers/messages/${profileHash}`}
                        className={'job-seeker-recruiter-message-button'}
                        onClick={() => {
                            EmitMetric({
                                misc_event_type:
                                    'message-button-click-professional-profile',
                                misc_event_count: 1,
                            });
                        }}
                    >
                        Message
                    </a>
                )}
                {!this.props.isAdmin && (
                    <div className="save-button">
                        Save Profile{' '}
                        <ReactionIcons
                            onClick={this.saveButtonHandler}
                            type={'like'}
                            active={this.state.saved}
                        />
                    </div>
                )}
            </li>
        );
    };

    saveButtonHandler = async () => {
        try {
            const updatedSaveValue = !this.state.saved;
            const savedValue = updatedSaveValue ? 1 : 0;
            await this.context.saveApplicant(
                this.props.companyId,
                this.props.profileHash,
                savedValue
            );
            this.setState({
                saved: updatedSaveValue,
            });
        } catch (e) {
            console.error(e);
        }
    };

    render() {
        const {
            profileHash,
            profileFields,
            hasVcfProps,
            vcfPreferenceProps,
        } = this.props;
        //@TODO adjust this logic....
        console.log('hasVcfProps', this.hasButtonSection(), hasVcfProps);
        const hasTopBorder = !!(this.hasButtonSection() || !hasVcfProps)
            ? 'has-top-border'
            : '';
        const hasSpacing =
            !this.hasButtonSection() && hasVcfProps ? 'has-top-spacing' : '';
        return (
            <div
                className={`component-Employer-VCFPortal-ProfileFieldList ${hasTopBorder} ${hasSpacing}`}
            >
                <ul className="field-list">
                    {!this.hasButtonSection()
                        ? null
                        : this.buttonSection(profileHash, true)}
                    {hasVcfProps && <VCFProfileCard {...vcfPreferenceProps} />}
                    {this.parseProfileFields(profileFields)}
                    {!this.hasButtonSection()
                        ? null
                        : this.buttonSection(profileHash)}
                </ul>
            </div>
        );
    }
}
